import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../components/context/authContext";
import { useNotifications } from "../../components/context/notificationContext";
import { isSuspendedFrom } from "../../utils/membersSiteAccessPermissions";
import { LeftArrow } from "../../components/icons";
import { SolidColorButton } from "../../components/buttons";
import FilmStrip from "./filmStrip";
import AnimatedPencil from "./animatedPencil";
import AnimatedTrophy from "./animatedTrophy";
import AnimatedLightBulb from "./animatedLightBulb";
import AnimatedStar from "./animatedStar";
import CurrentAveragedRating from "../../components/starRating/dashboardStarRating";
import { apiFetchRatedSkitsForCurrentUserDashboard } from "../../api/backend/skits";
import { categories } from "../../constants/categories";
import TooltipDots from "../../components/toolTipDots/toolTipDots";
import CurrentUserSkitDashboardMediumSmallDevice from "./CurrentUserSkitDashboardMediumSmallDevice";
import Loader from "../../components/loader/loader";
import "./currentUserSkitDashboard.scss";

const getCategoryById = (id) => {
  return categories.find((category) => category.id === id);
};

const CurrentUserSkitDashboard = () => {
  const { currentMemberId, handleLogout } = useAuth();
  const [ratedSkits, setRatedSkits] = useState([]);
  const { notificationsCountFetchedFromMiddleware } = useNotifications();
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const limit = 10;

  const fetchRatedSkits = async (currentOffset = 0) => {
    if (!currentMemberId) return;
    setIsLoading(true);
    const loggedInAccountHolderId = currentMemberId;
    try {
      const resp = await apiFetchRatedSkitsForCurrentUserDashboard(
        loggedInAccountHolderId,
        limit,
        currentOffset
      );
      // suspension eject
      if (isSuspendedFrom(resp) || isSuspendedFrom(resp?.data)) {
        await handleLogout?.();
        return;
      }
      // 🔔 hydrate unread (body first)
      const bodyCount = Number(
        resp?.data?.unreadNotificationCount ?? resp?.unreadNotificationCount
      );
      if (Number.isFinite(bodyCount)) {
        notificationsCountFetchedFromMiddleware(bodyCount);
      }
      // 🔔 header fallback
      const headerCount = Number(resp?.headers?.["x-unread-notifications"]);
      if (Number.isFinite(headerCount)) {
        notificationsCountFetchedFromMiddleware(headerCount);
      }
      // tolerant parse: backend returns an array, but be flexible
      const body = resp?.data !== undefined ? resp.data : resp;
      const skitsArray = Array.isArray(body)
        ? body
        : Array.isArray(body?.skits)
          ? body.skits
          : Array.isArray(body?.data)
            ? body.data
            : [];
      if (currentOffset === 0) {
        setRatedSkits(skitsArray);
      } else {
        setRatedSkits((prev) => [...prev, ...skitsArray]);
      }
      setHasMore(skitsArray.length >= limit);
    } catch (error) {
      // 🔔 hydrate from error payload if present
      const errCount =
        error?.response?.data?.unreadNotificationCount ??
        error?.unreadNotificationCount ??
        null;
      if (typeof errCount === "number") {
        notificationsCountFetchedFromMiddleware(errCount);
      }
      // suspension eject (error path)
      if (isSuspendedFrom(error) || isSuspendedFrom(error?.response)) {
        await handleLogout?.();
        return;
      }
      console.error("Error fetching rated skits:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Reset pagination when currentMemberId changes
    console.log("[Dashboard] init effect with memberId=", currentMemberId);
    setOffset(0);
    setHasMore(true);
    fetchRatedSkits(0);
  }, [currentMemberId]);

  const handleViewMore = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    fetchRatedSkits(newOffset);
  };

  return (
    <>
      <div id="top" className="user-skit-dashboard-desktop-view">
        <SolidColorButton
          solidColorBtnImage={
            <LeftArrow
              height="15px"
              width="15px"
              fillColor="#964b00"
              strokeWidth="4"
              strokeColor="#964b00"
            />
          }
          className="gold-btn"
          solidColorBtnText="Back"
          solidColorBtnFontWeight="800"
          solidColorBtnFontFamily="helvetica"
          solidColorBtnTextFontSize=".75rem"
          solidColorBtnFontColor="#964b00"
          solidColorBtnWidth="100px"
          solidColorBtnBoxShadow="0 4px 3px 1px #000000bb, 0 6px 8px #000000bb, 0 -4px 4px #000000bb, 0 -6px 4px #000000bb, inset 0 0 3px 0 #000000bb"
          onClick={() => navigate(-1)}
        />
        {/* Display loader if loading and no skits are loaded yet */}
        {isLoading && ratedSkits.length === 0 ? (
          <Loader />
        ) : (
          <div>
            {ratedSkits.length > 0
              ? ratedSkits.map((skit) => {
                  const category = getCategoryById(skit.categoryid);
                  return (
                    <div key={skit.id} className="skitDesktop-icon-wrapper">
                      <FilmStrip
                        title={
                          <TooltipDots
                            text={skit.title}
                            maxLength={33} // dynamic truncation before the "..."
                          />
                        }
                      />
                      <div className="skitDesktop-icon-container">
                        <AnimatedPencil />
                        <CurrentAveragedRating
                          rating={skit.storyLineAverageScore}
                        />
                        <p className="story-line">Story Line</p>
                        <p className="story-line-description">
                          (meaning, sequence, events)
                        </p>
                      </div>
                      <div className="skitDesktop-icon-container">
                        <AnimatedStar />
                        <CurrentAveragedRating
                          rating={skit.performanceAverageScore}
                        />
                        <p className="performance">Actor's/(s') Performance</p>
                        <p className="performance-description">
                          (Believable Character(s), Reactional timing)
                        </p>
                      </div>
                      <div className="skitDesktop-icon-container">
                        <AnimatedLightBulb />
                        <CurrentAveragedRating
                          rating={skit.originalityAverageScore}
                        />
                        <p className="originality">Originality</p>
                        <p className="originality-description">
                          (How original is the skit)
                        </p>
                      </div>
                      <div className="skitDesktop-icon-container">
                        <AnimatedTrophy />
                        <CurrentAveragedRating
                          rating={skit.categoryAverageScore}
                        />
                        <p className="category-title">{category.title}</p>
                        <p className="category-description">
                          {category.description}
                        </p>
                      </div>
                    </div>
                  );
                })
              : // Only show the "No Skit Metrics Found" message when not loading.
                !isLoading && (
                  <div className="skitDesktop-icon-wrapper">
                    <FilmStrip title="Skit Title" />
                    <h2 style={{ position: "absolute", top: "0px" }}>
                      No Skit Metrics Found..
                    </h2>
                    <div className="skitDesktop-icon-container">
                      <AnimatedPencil />
                      <CurrentAveragedRating rating={0} />
                      <p className="story-line">Story Line</p>
                      <p className="story-line-description">
                        (meaning, sequence, events)
                      </p>
                    </div>
                    <div className="skitDesktop-icon-container">
                      <AnimatedStar />
                      <CurrentAveragedRating rating={0} />
                      <p className="performance">Actor's/(s') Performance</p>
                      <p className="performance-description">
                        (Believable Character(s), Reactional timing)
                      </p>
                    </div>
                    <div className="skitDesktop-icon-container">
                      <AnimatedLightBulb />
                      <CurrentAveragedRating rating={0} />
                      <p className="originality">Originality</p>
                      <p className="originality-description">
                        (How original is the skit)
                      </p>
                    </div>
                    <div className="skitDesktop-icon-container">
                      <AnimatedTrophy />
                      <CurrentAveragedRating rating={0} />
                      <p className="category-title">Dynamic Category</p>
                      <p className="category-description">
                        (Dynamic Description)
                      </p>
                    </div>
                  </div>
                )}
          </div>
        )}

        {/* "View More" button is only shown if there are skits, more data is available, and we're not loading */}
        {!isLoading && ratedSkits.length > 0 && hasMore && (
          <button onClick={handleViewMore}>View More</button>
        )}
      </div>
      {/* Render the small/medium devices component */}
      <CurrentUserSkitDashboardMediumSmallDevice />
    </>
  );
};

export default CurrentUserSkitDashboard;
